import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AppstoreAddOutlined,
  FileImageOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import {
  Space,
  Card,
  Layout,
  Form,
  Input,
  Checkbox,
  Button,
  Select,
  Col,
  Row,
  Table,
  message,
  Upload,
  Avatar,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import SideBar from "./_sideBar";
import CopyRight from "./CopyRight";

const { Content } = Layout;

function MerchantForm() {
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState({
    name: "",
    username: "",
    password: "",
    secretKey: "",
    callbackUrl: "",
    roles: null,
    isEnabled: false,
  });

  const [form] = Form.useForm();
  const [menuDisabled, setMenuDisabled] = useState(false);
  const [nextMenuDisabled, setNextMenuDisabled] = useState(false);
  const [merchantList, setMerchantList] = useState();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [loginUser, setLoginUser] = useState();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const handleChange = (e) => {};

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Prepare the payload to send to the backend
  const preparePayload = async (values) => {
    // Convert files to base64
    const fileBase64Promises = fileList.map((file) =>
      convertFileToBase64(file.originFileObj)
    );
    const fileBase64Data = await Promise.all(fileBase64Promises);

    return {
      merchant: {
        name: values.merchantName,
        callbackUrl: values.merchantCallbackUrl,
        isEnabled: values.merchantIsEnabled || false,
        roles: values.merchantRole,
      },
      user: {
        name: values.name,
        username: values.username,
        password: values.userPassword,
        callbackUrl: values.userCallbackUrl,
        isEnabled: values.userIsEnabled || false,
        userRole: values.userRole,
      },
      fileImages: fileBase64Data, // Add the base64-encoded files
    };
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const payload = await preparePayload(values);

      const merchantResponse = await axios.post(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/merchant/saveMerchant`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        }
      );

      // Save User with reference to Merchant ID
      message.success("Merchant and User saved successfully!");
      fetchMerchants();
      setFileList([]);
      form.resetFields();
    } catch (error) {
      console.error("Error saving data:", error);
      message.error("Failed to save data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("loginUser"));
    setLoginUser(user);
    fetchMerchants();

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const loginUserToken = JSON.parse(localStorage.getItem("loginUserToken"));

  const fetchMerchants = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BACKEND_URL}/v1/merchant/findAllMerchant`,
        {
          headers: {
            Authorization: `Bearer ${loginUserToken}`,
          },
        }
      );

      const processedData = processMerchantData(response.data);
      setMerchantList(processedData);
    } catch (error) {
      console.error("Error fetching merchants:", error);
    }
  };

  const processMerchantData = (data) => {
    return data.map((item, index) => ({
      key: index,
      name: item.merchant?.name || "",
      username: item.user?.username || "",
      callbackUrl: item.merchant?.callbackUrl || "",
      roles: item.user?.userRole || "",
      isEnabled: item.user?.isEnabled,
      id: item.merchant?.id,
    }));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Callback URL",
      dataIndex: "callbackUrl",
      key: "callbackUrl",
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "roles",
    },
    {
      title: "Is Enabled",
      dataIndex: "isEnabled",
      key: "isEnabled",
      render: (isEnabled) => (isEnabled ? "Yes" : "No"), // Display Yes/No
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div>
          <Button
            type="link"
            onClick={() => navigate(`/subMerchant/${record.id}`)}
          >
            Sub-Merchant
          </Button>
          <Button
            type="link"
            onClick={() => navigate(`/merchantBank/${record.id}`)}
          >
            Bank
          </Button>
        </div>
      ),
    },
  ];

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList.slice(-1));
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <SideBar
        menuDisabled={menuDisabled}
        nextMenuDisabled={nextMenuDisabled}
        fontSize="14px"
        drawerVisibleValue={drawerVisible}
        setDrawerVisibleValue={setDrawerVisible}
      />

      <Layout className="site-layout">
        <Content>
          <div className="site-layout-background" style={{ minHeight: 360 }}>
            <Space
              direction="vertical"
              style={{ padding: "10px", width: "100%" }}
            >
              <Card
                title={
                  <span>
                    {isMobile && (
                      <MenuOutlined
                        onClick={() => setDrawerVisible((prev) => !prev)}
                        style={{ marginRight: 8, fontSize: 30 }}
                      />
                    )}
                    <AppstoreAddOutlined style={{ marginRight: 8 }} />
                    Merchant Setup
                  </span>
                }
                headStyle={{ backgroundColor: "#cccccc" }}
                extra={
                  <span>
                    {loginUser?.username}
                    <Avatar
                      src={loginUser?.avatarUrl}
                      icon={!loginUser?.avatarUrl && <UserOutlined />}
                      style={{ marginLeft: 8 }}
                    />
                  </span>
                }
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  style={{
                    maxWidth: "1000px",
                    margin: "0 auto",
                  }}
                >
                  <h2>Merchant</h2>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Merchant Name"
                        name="merchantName"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the merchant name",
                          },
                        ]}
                      >
                        <Input placeholder="Enter merchant name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Merchant Callback URL"
                        name="merchantCallbackUrl"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the callback URL",
                          },
                        ]}
                      >
                        <Input placeholder="Enter callback URL" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="merchantIsEnabled"
                        valuePropName="checked"
                      >
                        <Checkbox>Merchant Enabled</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>

                  <h2>User </h2>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Name"
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the user name",
                          },
                        ]}
                      >
                        <Input placeholder="Enter user name" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Username"
                        name="username"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the username",
                          },
                        ]}
                      >
                        <Input placeholder="Enter username" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Password"
                        name="userPassword"
                        rules={[
                          {
                            required: true,
                            message: "Please enter the password",
                          },
                        ]}
                      >
                        <Input.Password placeholder="Enter password" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Role"
                        name="userRole"
                        rules={[
                          {
                            required: true,
                            message: "Please select a role",
                          },
                        ]}
                      >
                        <Select
                          id="userRole"
                          placeholder="Select Role"
                          onChange={(value) =>
                            handleChange({
                              target: {
                                name: "userRole",
                                value,
                              },
                            })
                          }
                        >
                          <Select.Option value="ADMIN">Admin</Select.Option>
                          <Select.Option value="USER">User</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* File Upload */}
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Upload Image">
                        <Upload
                          accept=".jpg,.jpeg,.png"
                          beforeUpload={() => false} // Prevent auto-upload
                          onChange={handleUploadChange}
                          fileList={fileList}
                          maxCount={1} // Limit to one file
                        >
                          <Button icon={<FileImageOutlined />}>
                            Click to Upload
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item name="userIsEnabled" valuePropName="checked">
                        <Checkbox>User Enabled</Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Button type="primary" htmlType="submit" loading={loading}>
                    Submit
                  </Button>
                </Form>
              </Card>

              {/* Responsive Table */}
              <Table
                dataSource={merchantList}
                columns={columns}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                scroll={{ x: "max-content" }}
                bordered
                style={{ width: "100%" }}
              />
            </Space>
          </div>
          <CopyRight />
        </Content>
      </Layout>
    </Layout>
  );
}

export default MerchantForm;
